import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { Logo } from './Logo'
import { WaitlistForm } from './marketing/WaitlistForm'
import { Modal } from './ui/Modal'
import './marketing-chrome.css'

// Rebuilt 2026-10-08 (dark homepage rebuild, see TODO.md's "Rebuild the
// RemindLoop marketing homepage" entry) -- now a single committed dark
// look site-wide on every marketing page, not just "/", since this is a
// shared component (see MarketingShell). Centre links to homepage sections
// are plain <a>, not a react-router Link, so a click from a non-home page
// does a real navigation and gets the browser's native on-load hash scroll
// rather than silently landing on "/" with no scroll.
//
// Phone layout (added 2026-10-29, website-improvement item 2 and 3): under
// 800px the centre links collapse into a menu panel opened by a button, the
// join button shortens to "Join waitlist", and "Log in" moves into the panel,
// so the bar (logo, join, menu) fits a 375px screen. There is ONE <nav>: it
// is the inline row on desktop and the dropdown panel on phones. The waitlist
// dialog lives here, outside the panel, so closing the menu never unmounts it.
const centerLinks = [
  { href: '/#how', label: 'How it works' },
  { to: '/features', label: 'Features' },
  { href: '/#pricing', label: 'Pricing' },
  { href: '/#faq', label: 'FAQ' },
]

export function MarketingHeader() {
  const { user, isLoading } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [waitlistOpen, setWaitlistOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    const onPointer = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [menuOpen])

  const openWaitlist = () => {
    setMenuOpen(false)
    setWaitlistOpen(true)
  }

  return (
    <header
      ref={headerRef}
      className="rl-dark sticky z-40 border-b"
      style={{
        top: 'env(safe-area-inset-top, 0px)',
        // 94%, not the reference's lighter blend: this header is shared
        // across every marketing page (see this file's own comment), and
        // a lighter blend reads fine over the homepage's own dark hero
        // but washes out against a light page's content scrolling
        // beneath it -- a real Lighthouse contrast failure on /features
        // (the magenta-500 "Loop" wordmark at 2.46:1 against the
        // resulting blended color) caught during the 2026-10-08 rebuild,
        // not theoretical. 94% keeps a touch of translucency/blur while
        // staying close enough to solid var(--mkt-bg) to pass contrast
        // regardless of what's behind it.
        background: 'color-mix(in srgb, var(--mkt-bg) 94%, transparent)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderColor: 'var(--mkt-hairline)',
        color: 'var(--mkt-fg)',
      }}
    >
      <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between gap-3 px-4 min-[800px]:gap-5 min-[800px]:px-6">
        {/* No aria-label: the logo's own visible "RemindLoop" text is
            already a clear accessible name -- an aria-label here would
            override it with text that doesn't contain it (WCAG 2.5.3),
            flagged by a real Lighthouse audit during the 2026-10-08
            homepage rebuild. */}
        <Link to="/" className="flex flex-none items-center gap-2.5">
          <Logo reversed />
        </Link>

        <nav
          id="site-nav"
          className={`rl-sitenav${menuOpen ? ' rl-sitenav-open' : ''}`}
          aria-label="Main"
        >
          {centerLinks.map((item) =>
            'to' in item && item.to ? (
              <Link key={item.to} to={item.to} className="rl-navlink" onClick={() => setMenuOpen(false)}>
                {item.label}
              </Link>
            ) : (
              <a key={item.href} href={item.href} className="rl-navlink" onClick={() => setMenuOpen(false)}>
                {item.label}
              </a>
            ),
          )}
          {!isLoading && !user && (
            <Link
              to="/login"
              className="rl-navlink rl-navlink-phone"
              onClick={() => setMenuOpen(false)}
            >
              Log in
            </Link>
          )}
        </nav>

        <div className="flex flex-none items-center gap-3 text-[15px] min-[800px]:gap-5">
          {isLoading ? null : user ? (
            <Link to="/dashboard" className="rl-btn-cta rl-btn-cta-sm rl-header-cta">
              Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="rl-navlink rl-navlink-desktop" style={{ color: 'color-mix(in srgb, var(--mkt-fg) 80%, transparent)' }}>
                Log in
              </Link>
              <button type="button" className="rl-btn-cta rl-btn-cta-sm rl-header-cta" onClick={openWaitlist}>
                <span className="rl-label-long">Join the waitlist</span>
                <span className="rl-label-short">Join waitlist</span>
              </button>
            </>
          )}
          <button
            ref={menuButtonRef}
            type="button"
            className="rl-menu-btn"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {menuOpen ? <path d="M5 5l12 12M17 5L5 17" /> : <path d="M3 6h16M3 11h16M3 16h16" />}
            </svg>
          </button>
        </div>
      </div>
      <Modal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} title="Join the waitlist">
        <WaitlistForm source="header" />
      </Modal>
    </header>
  )
}
